/**
 * Shipping — label lifecycle FSM (LabelState) + tracking sub-FSM (TrackingState).
 *
 * @see .tondocs/shippo/PLAN.md §K.2 (LabelState — FSM as ADT)
 * @see .tondocs/shippo/PLAN.md §K.3 (TrackingState — nested FSM)
 * @see .tondocs/shippo/PLAN.md §K.10 (10-axis type-design audit — axes 4/5/7/8/10 lifted 2026-06-23)
 * @see .tondocs/shippo/PLAN.md §O.1.6–§O.1.10 (P0α structural locks)
 * @see .tondocs/shippo/PLAN.md §H.1 (label-state ladder driving the refund matrix)
 * @see .tondocs/shippo/PLAN.md §J.1.1 (the missing 'cancelled' enum value this lifts)
 * @see .tondocs/shippo/PLAN.md §J.3.C (Shippo 90-day refund window — drives reversibility.until)
 * @see .tondocs/shippo/PLAN.md §L (sync ship-ready trigger — new captures land
 *      directly in `'purchased'` or `'failed'`; `'pending'`/`'purchasing'`
 *      are retained for legacy / recovery / OAuth-era rows only)
 * @see .tondocs/skills/type-driven-design.md §1, §2, §3, §9
 *      (ADT + illegal-states-unrepresentable + exhaustiveness + FSM)
 *
 * Replaces `seller_orders.label_status text + ~7 sibling nullable columns`
 * with a single tagged union. Each field lives only inside the variant that
 * owns it — `transactionId`/`labelUrl`/`trackingNumber` are physically
 * inaccessible unless TS has narrowed `kind === 'purchased'`.
 *
 * INVARIANT — keep all 7 variants. Even though §L kills the post-capture
 * async-buy worker (so new captures never write `'pending'`/`'purchasing'`),
 * the DB CHECK still admits those values for legacy / recovery / OAuth-era
 * rows. Dropping a variant here would make `parseLabelState` reject rows that
 * exist in prod today. Memory ref: HANDOFF "Key invariants #3".
 */

import type {
  BuyerPaid,
  CurrencyCode,
  VendorBilled,
} from "./types";

// ---------------------------------------------------------------------------
// TrackingState — nested FSM, sub-state of LabelState.purchased
// ---------------------------------------------------------------------------

/**
 * Mirrors Shippo `track_updated` payload `tracking_status` axis. Drives the
 * §H.1 label-state ladder (B vs C vs D vs E) for refund-matrix decisions.
 *
 * Mirrors the DB CHECK on `seller_orders.tracking_status` ∈
 * {'pre_transit', 'transit', 'out_for_delivery', 'delivered', 'returned', 'failure'}.
 *
 * Persisted: NULL until the first webhook arrives → first carrier scan flips
 * it to `'pre_transit'`. Carries event-time fields only where a downstream
 * decision needs them (delivery date for return-window math; failure reason
 * for operator triage).
 */
export type TrackingState =
  | { kind: "pre_transit" }
  | { kind: "transit"; lastEventAt: Date }
  | { kind: "out_for_delivery" }
  | { kind: "delivered"; deliveredAt: Date }
  | { kind: "returned"; returnedAt: Date }
  | { kind: "failure"; reason: string };

// ---------------------------------------------------------------------------
// LabelState — label lifecycle FSM
// ---------------------------------------------------------------------------

/**
 * Label lifecycle for one `seller_order`.
 *
 * Variants:
 *  - `'none'`       — legacy seller flat-rate flow OR live-rate awaiting the
 *                     seller's "Ready to ship" click (PLAN §L). No fields.
 *  - `'pending'`    — LEGACY/RECOVERY only: rate locked, async buy not started.
 *                     New captures never write this (§L killed the worker).
 *                     Kept because OAuth-era seller_orders still carry it and
 *                     the DB CHECK admits it.
 *  - `'purchasing'` — LEGACY/RECOVERY only: async buy in flight (Shippo 202
 *                     QUEUED → SUCCESS/ERROR). Reasoning identical to `'pending'`.
 *  - `'purchased'`  — Label bought, prepaid. ALL carrier fields present + a
 *                     nested {@link TrackingState} (NULL tracking_status maps
 *                     to `pre_transit`).
 *  - `'failed'`     — Sync buy errored at Ready-to-ship time (PLAN §L). Seller
 *                     UI shows retry. No external_transaction_id was produced.
 *  - `'cancelled'`  — Refund handler pre-empted the buy before it ran
 *                     (REFUND_RUNBOOK §3.1 single-flight gate). J.1.1: this
 *                     value did not exist in the CHECK before
 *                     `20260617120000_provider_neutral_shipping.sql`; the
 *                     `default: unreachable(...)` probe in parse-label-state
 *                     is what would have caught it at compile time.
 *  - `'refunded'`   — Label was bought, then voided via Shippo refund. The
 *                     refund amount is what Shippo actually credited (may be
 *                     less than `purchased.labelCost` per Shippo's policy).
 *
 * The `purchased` variant nests {@link TrackingState} so the 2-D ladder
 * `(LabelState.kind, TrackingState.kind)` in §H.1 reduces to one exhaustive
 * switch with a `default: unreachable(...)` probe.
 */
// ---------------------------------------------------------------------------
// ObservabilityHooks — optional mix-in on every non-`none` variant
// ---------------------------------------------------------------------------

/**
 * Optional vendor-payload / trace correlation fields. Mixed into every
 * non-`none` {@link LabelState} variant so a post-mortem on a stuck order
 * reduces to one parsed-state field lookup instead of grep-by-time across
 * Axiom.
 *
 * Free at MVP — all fields optional, no DB column required for Slice A. The
 * parser passes them through when the row carries them; default is
 * `undefined` everywhere. Storage (raw payload jsonb / blob ref) lands in a
 * later slice.
 *
 * @see .tondocs/shippo/PLAN.md §K.10 axis 8 (Observability / replay-ability)
 * @see .tondocs/shippo/PLAN.md §O.1.9 (P0α — observability hooks)
 */
export type ObservabilityHooks = {
  /** Vendor object id (e.g. Shippo `transaction.object_id`). */
  vendorPayloadRef?: string;
  /** Request correlation id propagated through the call chain. */
  traceId?: string;
  /** Provider webhook event id (when the state was set by a webhook). */
  webhookEventId?: string;
};

export type LabelState =
  | { kind: "none" }
  | ({
      kind: "pending";
      rateId: string;
      shipmentId: string;
    } & ObservabilityHooks)
  | ({
      kind: "purchasing";
      rateId: string;
      shipmentId: string;
      startedAt: Date;
    } & ObservabilityHooks)
  | ({
      kind: "purchased";
      transactionId: string;
      labelUrl: string;
      trackingNumber: string;
      /**
       * SoT-divergence pair (PLAN §O.1.6 / §K.10 axis 4):
       *  - `ourEstimate` is the buyer-quoted, buffered rate locked at checkout
       *    (sourced from `seller_orders.external_rate_estimate_amount`). It is
       *    `null` on legacy `purchased` rows captured before the estimate
       *    column is populated (Slice C migration will start writing it; new
       *    writes will populate, legacy stays `null`).
       *  - `vendorActual` is what Shippo actually billed Kraahl (sourced from
       *    `seller_orders.label_cost`). Always present on `purchased`. Its
       *    currency is sourced from `seller_orders.label_cost_currency`
       *    (separate axis from `seller_orders.currency` — F-12, 2026-06-24).
       *
       * The two sides MAY differ in currency: a UK Limited-tier seller takes a
       * USD buyer's order → `ourEstimate.currency` follows the buyer-paid
       * currency (seller_orders.currency), `vendorActual.currency` follows
       * what Shippo billed Kraahl (seller-native, GBP). The currency-pin
       * trigger keeps `seller_orders.currency` ALIGNED with `profiles.currency`
       * — for US-only MVP both sides collapse to USD; the second axis is
       * reserved for the future Limited-tier flow.
       *
       * UI for buyer reads `ourEstimate`; `decideRefundAction` / margin
       * accounting reads `vendorActual`.
       */
      labelCost: {
        ourEstimate: BuyerPaid<CurrencyCode> | null;
        vendorActual: VendorBilled<CurrencyCode>;
      };
      carrier: string;
      servicelevel: string;
      purchasedAt: Date;
      tracking: TrackingState;
    } & ObservabilityHooks)
  | ({
      kind: "failed";
      reason: string;
      attemptedAt: Date;
    } & ObservabilityHooks)
  | ({
      kind: "cancelled";
      cancelledAt: Date;
      reason: string;
    } & ObservabilityHooks)
  | ({
      kind: "refunded";
      refundedAt: Date;
      /**
       * Shippo void credit (PLAN §K.4.1) — what the provider actually
       * credited back, may be less than the prior `purchased.labelCost.vendorActual`
       * per provider policy. Branded as {@link VendorBilled} because the
       * money flows vendor→Kraahl, not buyer→Kraahl. No estimate counterpart
       * — voids have no "what the buyer would have been quoted" mirror.
       */
      refundAmount: VendorBilled<CurrencyCode>;
    } & ObservabilityHooks);

// ---------------------------------------------------------------------------
// Reversibility — computed property attached at parse-on-boundary
// ---------------------------------------------------------------------------

/**
 * Where in the refund-action ladder a given {@link LabelState} sits.
 *
 * Driven by §H.1's 5×4 (`label_status` × `tracking_status`) matrix; lifting
 * the matrix into a computed property gives compile-time exhaustiveness for
 * the §H.2 `decideRefundAction` switch (each new `Reversibility` variant
 * trips the `default: unreachable(...)` probe at every consumer).
 *
 * Variants:
 *  - `reversible_via_void` — label bought, NO carrier scan yet (`purchased`
 *    + `pre_transit`). `until` = `purchasedAt + 90 days` per PLAN §J.3.C
 *    (Shippo's hard refund window). After `until`, downgrades to
 *    `point_of_no_return` at the consumer's discretion (parser only encodes
 *    "still within the window when parsed").
 *  - `reversible_pending` — buy hasn't fired yet (`pending` / `purchasing`).
 *    The refund handler can pre-empt the buy with a single-flight gate
 *    (REFUND_RUNBOOK §3.1). No `until` — there is no time pressure until
 *    the buy actually starts.
 *  - `point_of_no_return` — label bought, carrier has scanned or delivered
 *    (`purchased` + `out_for_delivery` | `delivered` | `returned` | `failure`).
 *    Refund requires inbound RMA, not a Shippo void.
 *  - `terminal` — `none`, `failed`, `cancelled`, `refunded`. Nothing to
 *    reverse; either the label was never bought or the lifecycle already
 *    closed.
 *
 * @see .tondocs/shippo/PLAN.md §K.10 axis 5 (Reversibility computed property)
 * @see .tondocs/shippo/PLAN.md §O.1.7 (P0α — reversibility lift)
 * @see .tondocs/shippo/PLAN.md §H.1 (label-state ladder — the source matrix)
 * @see .tondocs/shippo/PLAN.md §J.3.C (Shippo 90-day refund window)
 */
export type Reversibility =
  | { kind: "reversible_via_void"; until: Date }
  | { kind: "reversible_pending" }
  | { kind: "point_of_no_return" }
  | { kind: "terminal" };

// ---------------------------------------------------------------------------
// Exhaustiveness probe
// ---------------------------------------------------------------------------

/**
 * Re-exported from the canonical `lib/type-helpers.ts` so existing
 * `import { unreachable } from "@/lib/shipping/label-state"` call-sites keep
 * compiling. Adding an 8th variant to `LabelState` (or a 7th to
 * `TrackingState`) still trips the `never`-probe at every consumer's switch
 * default arm. See the helper's own docstring for the full rationale.
 *
 * @see .tondocs/skills/type-driven-design.md §3 (Exhaustiveness checking with `never`)
 */
export { unreachable } from "@/lib/type-helpers";
